import type {
  Workspace,
  WorkspaceCreate,
  WorkspaceUpdate,
  PaginatedResponse,
  ListWorkspacesParams,
  Item,
} from '../types';
import type { TasksDashboard } from '../../../generated/api/models/tasksDashboard';
import type { TasksMyDashboard } from '../../../generated/api/models/tasksMyDashboard';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/api/axiosInstance';

export interface ListWorkspaceTasksParams {
  search?: string;
  include_subitems?: boolean;
  limit?: number;
  offset?: number;
}

const API_BASE = '/api/v1/workspaces';

export const workspaceApi = {
  list: async (params?: ListWorkspacesParams): Promise<PaginatedResponse<Workspace>> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Workspace>>(url);
  },

  get: async (
    id: string,
    options?: { includeBoards?: boolean; includeMembers?: boolean }
  ): Promise<{ data: Workspace }> => {
    const searchParams = new URLSearchParams();
    if (options?.includeBoards) searchParams.set('include_boards', 'true');
    if (options?.includeMembers) searchParams.set('include_members', 'true');

    const url = `${API_BASE}/${id}${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<{ data: Workspace }>(url);
  },

  create: async (data: WorkspaceCreate): Promise<{ data: Workspace }> => {
    return apiPost<{ data: Workspace }>(API_BASE, data);
  },

  update: async (id: string, data: WorkspaceUpdate): Promise<{ data: Workspace }> => {
    return apiPut<{ data: Workspace }>(`${API_BASE}/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/${id}`);
  },

  listItems: async (
    workspaceId: string,
    params?: ListWorkspaceTasksParams
  ): Promise<PaginatedResponse<Item>> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.include_subitems) searchParams.set('include_subitems', 'true');
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/${workspaceId}/tasks${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Item>>(url);
  },

  countItems: async (
    workspaceId: string,
    params?: { include_subitems?: boolean }
  ): Promise<number> => {
    const searchParams = new URLSearchParams();
    if (params?.include_subitems) searchParams.set('include_subitems', 'true');

    const url = `${API_BASE}/${workspaceId}/tasks/count${searchParams.toString() ? `?${searchParams}` : ''}`;
    const data = await apiGet<{ count: number }>(url);
    return data.count ?? 0;
  },
};

// Dashboard API — schema-first response models (no `{ data }` envelope): the
// response body IS the model. See docs/platform/dashboard-kpi-endpoints.md.
export const dashboardApi = {
  get: async (): Promise<TasksDashboard> => {
    return apiGet<TasksDashboard>(`${API_BASE}/dashboard`);
  },
};

export const myDashboardApi = {
  get: async (workspaceId?: string, scope?: 'my' | 'all'): Promise<TasksMyDashboard> => {
    const searchParams = new URLSearchParams();
    if (workspaceId) searchParams.set('workspace_id', workspaceId);
    if (scope && scope !== 'my') searchParams.set('scope', scope);
    const url = `${API_BASE}/my-dashboard${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<TasksMyDashboard>(url);
  },
};
